"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  BookOpen,
  GraduationCap,
  BrainCircuit,
  Target,
  Award,
  Globe,
  ChevronRight,
  ChevronLeft,
  Check,
  MessageSquare,
  Calculator,
  Sigma,
  FlaskConical,
  NotebookPen,
  Search,
  Users,
  Heart,
  MessageCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { clsx } from "clsx";
import { Mascot } from "@/components/Mascot";
import { HeroProductDemo } from "@/components/HeroProductDemo";
import VideoBackdrop from "@/app/about/VideoBackdrop";
import { apiFetch, versioned } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useMetadata } from "@/hooks/useMetadata";

type PhoneScreen = "home" | "ask" | "learn" | "community";
type LandingStats = {
  people: number | null;
  students: number | null;
  chunks: number | null;
  courses: number | null;
  notes: number | null;
};

function formatCount(value: number) {
  return new Intl.NumberFormat("en-NG").format(value || 0);
}

const EMPTY_LANDING_STATS: LandingStats = {
  people: null,
  students: null,
  chunks: null,
  courses: null,
  notes: null,
};

function normalizeStat(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function statText(value: number | null | undefined) {
  return value === null || value === undefined ? "—" : `${formatCount(value)}+`;
}

const Trophy = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

const LANDING_COPY = {
  hero: {
    headline: "Master Your Semester with AI-Powered Academic Precision",
    subheadline:
      "UniUI is an AI-powered academic platform for Nigerian university students. It ingests your lecture notes, past questions, and textbooks, then delivers precise, source-cited answers in the Nigerian-university format across 2,500+ courses — built for students at universities across Nigeria.",
    cta1: "Begin Academic Journey",
    cta2: "Explore the Scholar Engine",
    guestCta: "Try as Guest",
    guestCtaDetail:
      "Preview 20 notes, 1 quest, and 1 AI answer per day — no sign-up required.",
  },
  features: {
    title: "Pedagogical Excellence, Engineered for African Universities",
    items: [
      {
        icon: BrainCircuit,
        title: "Curriculum-Aligned Retrieval",
        description:
          "Multi-stage cascade searches your materials, external textbooks, academic references, and curated internet sources—never fabricating an answer.",
      },
      {
        icon: GraduationCap,
        title: "Structured Nigerian-Professor Output",
        description:
          "5-section format with Direct Explanation, General Pattern, Worked Examples (≥3), Exam Tips, and Quick Revision Card.",
      },
      {
        icon: BookOpen,
        title: "Material Enrichment & Preservation",
        description:
          "Auto-chunking, pedagogical metadata extraction, and permanent storage in your personal knowledge base.",
      },
    ],
  },
  preview: {
    title: "Experience the Scholar Engine",
    subtitle: "All the tools you need to dominate your CGPA, powered by AI.",
  },
  workflow: {
    title: "From Lecture Hall to Exam Hall",
    steps: [
      {
        icon: GraduationCap,
        title: "Enrol",
        description:
          "Link your academic profile with department, level, and registered courses — built for students at universities across Nigeria.",
      },
      {
        icon: BookOpen,
        title: "Ingest",
        description:
          "Course representatives upload official lecture notes, slides, and past questions.",
      },
      {
        icon: BrainCircuit,
        title: "Query",
        description:
          "          Ask any question; the system retrieves and reasons over enriched materials.",
      },
      {
        icon: Trophy,
        title: "Excel",
        description:
          "Personalised study plans, exam readiness scores, and weak-topic analyses.",
      },
    ],
  },
  community: {
    title: "Join the Academic Movement",
    subtitle:
      "Connect with students, share knowledge, and climb the leaderboard.",
  },
  tools: {
    title: "All the Study Tools You Need",
    subtitle: "Everything curated for Nigerian university success.",
    items: [
      {
        icon: BrainCircuit,
        title: "Ask AI",
        description: "Any course question answered",
      },
      {
        icon: BookOpen,
        title: "Notes",
        description: "AI-summarized study materials",
      },
      {
        icon: Target,
        title: "Flashcards",
        description: "SM-2 spaced repetition",
      },
      {
        icon: Trophy,
        title: "Quiz",
        description: "Auto-generated practice tests",
      },
      {
        icon: Award,
        title: "Formula Sheet",
        description: "LaTeX-rendered equations",
      },
      {
        icon: Globe,
        title: "Community",
        description: "Peer discussions & reviews",
      },
    ],
  },
  pricing: {
    title: "Simple, Transparent Pricing",
    subtitle:
      "Pay for questions, not promises. Every answer costs one credit — here is what each tier does.",
    plans: [
      {
        id: "free",
        name: "Free Tier",
        tagline: "For the curious student",
        price: "₦0",
        pricePeriod: "",
        priceAlt: "5 questions / day",
        features: [
          "5 questions per day (3 per hour)",
          "Access to all study tools",
          "Community access",
          "Basic tracking",
        ],
        cta: "Get Started Free",
        ctaLink: "/auth/signup",
        recommended: false,
      },
      {
        id: "topup",
        name: "Top-Up",
        tagline: "For the serious student",
        price: "₦500",
        pricePeriod: "for 10 questions",
        priceAlt: "",
        features: [
          "10 question credits added to balance",
          "Priority responses",
          "Past questions library",
          "Advanced analytics",
        ],
        cta: "Top Up Now",
        ctaLink: "/pay",
        recommended: false,
      },
      {
        id: "exampass",
        name: "Exam Pass",
        tagline: "Best value — exam season",
        price: "₦2,000",
        pricePeriod: "2 weeks access",
        priceAlt: "15 questions / day · 5 / hour",
        features: [
          "Full notes section with AI",
          "AI-predicted exam questions",
          "Step-wise explanations",
          "10 flashcards / day · 25% rollover",
        ],
        cta: "Get Exam Pass",
        ctaLink: "/pay",
        recommended: true,
      },
      {
        id: "premium",
        name: "Premium",
        tagline: "For the everyday companion",
        price: "₦1,500",
        pricePeriod: "per month",
        priceAlt: "15 questions / day · 5 / hour",
        features: [
          "Unlimited flashcards",
          "Offline PWA access",
          "40 PDF downloads / month",
          "Notes section is view-only",
        ],
        cta: "Go Premium",
        ctaLink: "/pay",
        recommended: false,
      },
    ],
  },
  testimonials: {
    title: "What Students Say",
    items: [
      {
        name: "Tunde Kolawole",
        school: "UNILAG",
        quote:
          "UniUI AI explained concepts better than my actual lecturer. Exam prep has never been this easy.",
        rating: 5,
      },
      {
        name: "Adaeze Okonkwo",
        school: "University of Benin",
        quote:
          "The flashcard feature alone saved my GPA. SM-2 algorithm actually works for retention.",
        rating: 5,
      },
      {
        name: "Emeka Nwosu",
        school: "UNIZIK",
        quote:
          "Uploading my slides and getting AI summaries felt like cheating. Best kind of cheating.",
        rating: 5,
      },
    ],
  },
  FAQ: {
    title: "Frequently Asked Questions",
    subtitle: "Answers to common UniUI questions: how the Socratic AI tutor works, what makes an answer verified, how citations and course grounding work, plus plans, credits and account help.",
    questions: [
      {
        question: "Do unused questions expire?",
        answer:
          "Questions expire at the end of each semester. Use them or lose them.",
      },
      {
        question: "How accurate are the AI answers?",
        answer:
          "Our multi-stage cascade ensures 98% accuracy with source citations from your materials.",
      },
      {
        question: "Can I access past questions?",
        answer:
          "Yes, after verification. Contact your course rep to upload official materials.",
      },
    ],
  },
  trust: {
    title: "Validated by the African Academic Community",
  },
  cta_banner: {
    headline: "Begin Your Exam Preparation with Scholarly Rigour",
    cta: "Enrol Now",
    fine_print: "Free academic tier available. No payment required.",
  },
  footer: {
    tagline:
      "UniUI — Academic Intelligence for the Nigerian University Student.",
    links: ["About", "Contact", "Privacy", "Terms"],
    social: ["GitHub", "Twitter", "LinkedIn"],
    copyright: "© 2026 UniUI. Built for the African academic community.",
  },
};

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, isInView };
}

function SectionFade({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { ref, isInView } = useInView(0.15);
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${className}`}
    >
      {children}
    </div>
  );
}

function ScrollToButton({
  targetId,
  children,
}: {
  targetId: string;
  children: React.ReactNode;
}) {
  const handleClick = useCallback(() => {
    const el = document.getElementById(targetId);
    el?.scrollIntoView({ behavior: "smooth" });
  }, [targetId]);
  return <button onClick={handleClick}>{children}</button>;
}

type FloatGlyphProps = {
  children: React.ReactNode;
  className?: string;
  x?: number;
  y?: number;
  rotate?: number;
  duration?: number;
  delay?: number;
};

function FloatGlyph({
  children,
  className = "",
  x = 0,
  y = 0,
  rotate = 0,
  duration = 10,
  delay = 0,
}: FloatGlyphProps) {
  return (
    <motion.div
      className={className}
      animate={{ y: [0, y, 0], x: [0, x, 0], rotate: [0, rotate, 0] }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      {children}
    </motion.div>
  );
}

const FOOTER_LINK_HREF: Record<string, string> = {
  About: "/about",
  Contact: "/contact",
  Privacy: "/privacy",
  Terms: "/terms",
};

export default function LandingPage() {
  useMetadata({ key: "landing" });
  const { user, loading } = useAuth();
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [stats, setStats] = useState<LandingStats>(EMPTY_LANDING_STATS);
  const [statsState, setStatsState] = useState<"loading" | "ready" | "failed">(
    "loading",
  );
  const [phoneScreen, setPhoneScreen] = useState<PhoneScreen>("home");
  const [communityCourse, setCommunityCourse] = useState("All");
  const [communityCourses, setCommunityCourses] = useState<string[]>(["All"]);
  const [communityThreads, setCommunityThreads] = useState<
    Array<{
      id: string;
      title: string;
      tag: string;
      course: string;
      author: string;
      username: string;
      time: string;
      replies: number;
    }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    async function loadCommunityData() {
      try {
        const [threads, courses] = await Promise.all([
          apiFetch<any[]>(versioned("/api/v1/community/threads")).catch(
            () => [],
          ),
          apiFetch<string[]>(versioned("/api/v1/courses")).catch(() => []),
        ]);
        if (cancelled) return;
        const mappedThreads = (threads || []).slice(0, 6).map((t: any) => ({
          id: String(t?.id || Math.random()),
          title: String(t?.title || t?.body || "Discussion"),
          tag: String(t?.course_code || t?.course || "General"),
          course: String(t?.course_code || t?.course || "General"),
          author: String(
            t?.profiles?.full_name ||
              t?.profiles?.username ||
              t?.author_name ||
              "Scholar",
          ),
          username: String(t?.profiles?.username || t?.author_name || ""),
          time: String(t?.created_at || ""),
          replies:
            typeof t?.replies === "number"
              ? t.replies
              : typeof t?.reply_count === "number"
                ? t.reply_count
                : 0,
        }));
        setCommunityThreads(mappedThreads);
        const courseCodes =
          Array.isArray(courses) && courses.length > 0
            ? courses
            : mappedThreads.map((t) => t.course);
        setCommunityCourses([
          "All",
          ...Array.from(new Set(courseCodes)).slice(0, 8),
        ]);
      } catch {
        // keep_empty_state_on_failure
      }
    }
    loadCommunityData();
    return () => {
      cancelled = true;
    };
  }, []);
  const filteredCommunityThreads = communityThreads.filter(
    (thread) => communityCourse === "All" || thread.course === communityCourse,
  );
  const phoneScreens = {
    home: {
      label: "Home",
      title: "ALEX",
      subtitle: "Daily exam command centre",
    },
    ask: {
      label: "Ask AI",
      title: "Ask from notes",
      subtitle: "Source-cited answers",
    },
    learn: {
      label: "Learn",
      title: "Study library",
      subtitle: "Notes, flashcards, formulas",
    },
    community: {
      label: "Community",
      title: "Scholar feed",
      subtitle: "Course reps and leaderboards",
    },
  };
  const activePhoneScreen = phoneScreens[phoneScreen];
  const trustStats = [
    { display: statText(stats.students), label: "Enrolled Students" },
    {
      display: statText(stats.courses),
      label: "Courses Across Nigerian Universities",
    },
    { display: statText(stats.chunks), label: "Enriched Academic Chunks" },
    { display: statText(stats.notes), label: "Notes Available" },
    { display: statText(stats.people), label: "Community Members" },
  ];

  // Pointer-driven 3D tilt so the phone mockup feels like a held device.
  const phoneRef = useRef<HTMLDivElement>(null);
  const [phoneTilt, setPhoneTilt] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const handlePhoneTilt = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = phoneRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setPhoneTilt({ x: -py * 14, y: px * 14 });
  };
  const resetPhoneTilt = () => setPhoneTilt({ x: 0, y: 0 });

  useEffect(() => {
    Promise.resolve().then(() => {
      if (!loading) {
        if (user) {
          router.replace("/dashboard");
        } else {
          setMounted(true);
        }
      }
    });
  }, [user, loading, router]);

  useEffect(() => {
    if (!mounted || loading) return;

    let cancelled = false;

    async function fetchStats() {
      try {
        const data = await apiFetch<Partial<LandingStats>>(
          "/api/v1/landing/stats",
        );
        if (cancelled) return;
        setStats((current) => ({
          people: normalizeStat(data.people) ?? current.people,
          students:
            normalizeStat(data.students) ??
            normalizeStat(data.people) ??
            current.students,
          chunks: normalizeStat(data.chunks) ?? current.chunks,
          courses: normalizeStat(data.courses) ?? current.courses,
          notes: normalizeStat(data.notes) ?? current.notes,
        }));
        setStatsState("ready");
      } catch {
        if (!cancelled) {
          setStats(EMPTY_LANDING_STATS);
          setStatsState("failed");
        }
      }
    }

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [mounted, loading]);

  // Render the marketing content during SSR (user is null on the server) so
  // crawlers/bots see the app name and purpose. Only suppress when a logged-in
  // user is present (the effect above redirects them to /dashboard).
  if (user) return null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* -- ACADEMIC_HERO -- */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 overflow-visible pt-14 lg:pt-0">
        {/* About-style animated backdrop (drifting glyphs, orbital_rings) */}
        <VideoBackdrop className="pointer-events-none absolute inset-0 z-0" />
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">

          {/* drifting formulas */}
          <FloatGlyph
            className="absolute left-[8%] top-[20%] font-mono text-3xl text-y/15"
            x={10}
            y={-18}
            duration={9}
          >
            ∫ x² dx
          </FloatGlyph>
          <FloatGlyph
            className="absolute right-[10%] top-[26%] font-mono text-2xl text-y/15"
            x={-12}
            y={16}
            duration={11}
            delay={1}
          >
            ∑ n
          </FloatGlyph>
          <FloatGlyph
            className="absolute left-[13%] bottom-[18%] font-mono text-2xl text-y/15"
            x={14}
            y={-14}
            duration={10}
            delay={0.5}
          >
            ∂/∂x
          </FloatGlyph>
          <FloatGlyph
            className="absolute right-[11%] bottom-[20%] font-mono text-3xl text-y/15"
            x={-10}
            y={-16}
            duration={12}
            delay={1.5}
          >
            ƒ(x)=x³
          </FloatGlyph>
          <FloatGlyph
            className="absolute left-[44%] top-[12%] font-mono text-xl text-y/10"
            x={8}
            y={14}
            duration={8}
            delay={0.8}
          >
            ∇·E
          </FloatGlyph>
          <FloatGlyph
            className="absolute right-[42%] bottom-[12%] font-mono text-xl text-y/10"
            x={-8}
            y={12}
            duration={9}
            delay={2}
          >
            π r²
          </FloatGlyph>
          <FloatGlyph
            className="absolute left-[30%] bottom-[10%] font-mono text-lg text-y/10"
            x={12}
            y={-10}
            duration={10}
            delay={1.2}
          >
            a²+b²=c²
          </FloatGlyph>

          {/* drifting icons */}
          <FloatGlyph
            className="absolute left-[19%] top-[14%] text-y/20"
            x={12}
            y={18}
            duration={10}
          >
            <GraduationCap size={48} strokeWidth={1} />
          </FloatGlyph>
          <FloatGlyph
            className="absolute right-[18%] top-[16%] text-y/20"
            x={-14}
            y={16}
            duration={11}
            delay={1}
          >
            <BookOpen size={44} strokeWidth={1} />
          </FloatGlyph>
          <FloatGlyph
            className="absolute left-[25%] bottom-[15%] text-y/20"
            x={10}
            y={-16}
            duration={12}
            delay={1.5}
          >
            <BrainCircuit size={42} strokeWidth={1} />
          </FloatGlyph>
          <FloatGlyph
            className="absolute right-[23%] bottom-[17%] text-y/20"
            x={-10}
            y={-14}
            duration={9}
            delay={0.6}
          >
            <Calculator size={40} strokeWidth={1} />
          </FloatGlyph>
          <FloatGlyph
            className="absolute left-[38%] top-[64%] text-y/15"
            x={-12}
            y={12}
            duration={13}
            delay={0.9}
          >
            <Sigma size={34} strokeWidth={1} />
          </FloatGlyph>
          <FloatGlyph
            className="absolute right-[34%] top-[60%] text-y/15"
            x={12}
            y={-12}
            duration={11}
            delay={1.7}
          >
            <FlaskConical size={34} strokeWidth={1} />
          </FloatGlyph>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl mx-auto"
        >
          <div className="flex justify-center mb-6">
            <Mascot state="strict" size="large" className="animate-fade-in" />
          </div>
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-y/15 border border-y/30 text-xs font-black uppercase tracking-[0.25em] text-y shadow-sm">
              UniUI — Academic Intelligence Platform
            </span>
          </div>
          <h1
            className="font-display text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 text-foreground animate-slide-up"
            style={{ animationDelay: "0.3s" }}
          >
            UniUI — Master Your Semester with AI-Powered Academic Precision
          </h1>

          {/* Google OAuth & Trust Verification Purpose Card */}
          <div className="my-6 p-6 rounded-2xl bg-card/90 border border-y/40 text-left shadow-md max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 bg-y text-black font-black text-xs uppercase tracking-widest rounded-md">
                Application: UniUI
              </span>
              <span className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                Purpose & Scope
              </span>
            </div>
            <p className="text-sm text-foreground/90 leading-relaxed">
              <strong>UniUI</strong> is an AI-driven academic intelligence and
              study platform for Nigerian university students (200L & 300L). It
              connects students with syllabus-aligned past questions, AI-powered
              course-material explanations, flashcards, study schedules, and
              verified department communities.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/auth/signup"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-y text-black font-bold text-sm uppercase tracking-[0.15em] rounded-xl transition-all hover:shadow-[0_0_24px_rgba(255,214,0,0.35)] hover:ring-2 hover:ring-y/60 hover:scale-105 animate-pulse-gold w-full sm:w-auto"
            >
              {LANDING_COPY.hero.cta1}
              <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              href="/ask"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-border rounded-xl font-bold text-sm uppercase tracking-[0.15em] transition-all hover:bg-y hover:text-black hover:border-y hover:scale-105 w-full sm:w-auto"
            >
              {LANDING_COPY.hero.guestCta}
            </Link>
            <Link
              href="/auth/login"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-border rounded-xl font-bold text-sm uppercase tracking-[0.15em] transition-all hover:bg-y hover:text-black hover:border-y hover:scale-105 w-full sm:w-auto"
            >
              {LANDING_COPY.hero.cta2}
            </Link>
          </div>
          <p className="mt-4 text-xs text-foreground/70 font-medium">
            {LANDING_COPY.hero.guestCtaDetail}
          </p>
        </motion.div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-6 h-6 rotate-90 text-foreground" />
        </div>
      </section>

      {/* -- PEDAGOGICAL_FEATURES -- */}
      <SectionFade>
        <section id="features" className="py-24 px-6 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-black text-center mb-16 tracking-tight">
              {LANDING_COPY.features.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {LANDING_COPY.features.items.map((item, i) => (
                <div
                  key={i}
                  className="group relative p-8 rounded-2xl bg-card border border-border transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(0,0,0,0.12)] hover:border-y"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-y/10 text-y mb-6 transition-transform group-hover:scale-110">
                    <item.icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- SCHOLARLY_WORKFLOW -- */}
      <SectionFade>
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-black text-center mb-20 tracking-tight">
              {LANDING_COPY.workflow.title}
            </h2>

            {/* Desktop: horizontal timeline */}
            <div className="hidden md:block relative">
              <div className="absolute top-8 left-0 right-0 h-0.5 bg-border mx-20" />
              <div className="grid grid-cols-4 gap-8 relative">
                {LANDING_COPY.workflow.steps.map((step, i) => (
                  <div key={i} className="text-center">
                    <div className="relative z-10 w-16 h-16 rounded-full bg-y text-black flex items-center justify-center mx-auto mb-6 shadow-lg ring-4 ring-background">
                      <step.icon className="w-7 h-7" strokeWidth={2.5} />
                    </div>
                    <div className="hidden lg:block absolute top-8 left-1/2 -translate-x-1/2 mt-3 text-xs font-bold text-foreground bg-background px-2">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className="text-base font-bold mb-2 text-foreground">
                      {step.title}
                    </h3>
                    <p className="text-xs text-foreground leading-relaxed max-w-[220px] mx-auto">
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile: vertical timeline */}
            <div className="md:hidden relative pl-16 border-l-2 border-border">
              {LANDING_COPY.workflow.steps.map((step, i) => (
                <div key={i} className="relative mb-12 last:mb-0">
                  <div className="absolute -left-10 top-0 w-10 h-10 rounded-full bg-y text-black flex items-center justify-center shadow-lg">
                    <step.icon className="w-5 h-5" strokeWidth={2.5} />
                  </div>
                  <div className="text-xs font-bold text-y mb-1 tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="text-base font-bold mb-1 text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- APP_PREVIEW -- */}
      <SectionFade>
        <section id="preview" className="py-24 px-6 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-4">
                {LANDING_COPY.preview.title}
              </h2>
              <p className="text-base text-foreground max-w-2xl mx-auto">
                {LANDING_COPY.preview.subtitle}
              </p>
            </div>

            <div
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
              style={{ perspective: "1000px" }}
              onMouseMove={handlePhoneTilt}
              onMouseLeave={resetPhoneTilt}
            >
              <motion.div
                ref={phoneRef}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                animate={{
                  y: [0, -16, 4, -10, 0],
                  rotate: [-1.4, 1.4, -0.8, 1.2, -1.4],
                  scale: [1, 1.015, 0.992, 1.01, 1],
                }}
                transition={{
                  delay: 0.1,
                  default: { duration: 0.8, ease: "easeOut" },
                  y: { duration: 8, repeat: Infinity, ease: "easeInOut" },
                  rotate: { duration: 11, repeat: Infinity, ease: "easeInOut" },
                  scale: { duration: 6.5, repeat: Infinity, ease: "easeInOut" },
                }}
                style={{
                  rotateX: phoneTilt.x,
                  rotateY: phoneTilt.y,
                  transformStyle: "preserve-3d",
                }}
                className="relative mx-auto w-[310px] h-[640px] rounded-[46px] bg-black p-[10px] shadow-[0_35px_90px_rgba(0,0,0,0.35)] border border-y/30"
              >
                <div className="absolute left-1/2 top-[10px] h-[28px] w-[112px] -translate-x-1/2 rounded-b-2xl bg-black z-10" />
                <div className="absolute left-0 top-[110px] h-[34px] w-[3px] rounded-r bg-zinc-900" />
                <div className="absolute right-0 top-[132px] h-[48px] w-[3px] rounded-l bg-zinc-900" />
                <div className="relative h-full rounded-[38px] overflow-hidden bg-[#080808]">
                  <div className="h-[18px]" />
                  <div className="flex justify-between px-6 pt-1 text-xs font-black uppercase tracking-widest text-white">
                    <span>9:41</span>
                    <span>●●●</span>
                  </div>

                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={phoneScreen}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.22 }}
                      className="px-4 pt-2 pb-24 space-y-3"
                    >
                      {/* iOS Status Bar */}
                      <div className="flex justify-between items-center px-2 pt-1 pb-2">
                        <span className="text-[10px] font-black text-white">9:41</span>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded-full bg-white/20" />
                          <div className="w-3 h-3 rounded-full bg-white/20" />
                          <div className="w-3 h-3 rounded-full bg-white/20" />
                        </div>
                      </div>

                      {phoneScreen === "home" && (
                        <>
                          {/* Daily Question Banner */}
                          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-y via-[#ffe86b] to-[#f5c400] p-4 shadow-lg border border-y/30">
                            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-black/10" />
                            <div className="absolute -left-8 bottom-[-20px] h-28 w-28 rounded-full bg-black/10" />
                            <p className="relative text-[9px] font-black uppercase tracking-[0.3em] text-black mb-1">
                              Daily Question
                            </p>
                            <p className="relative font-display text-lg font-black text-black leading-tight mb-3">
                              Master Today&apos;s Exam Question
                            </p>
                            <div className="relative flex items-center gap-2">
                              <span className="rounded-full bg-black px-3 py-1.5 text-[9px] font-black uppercase tracking-wider text-y shadow-sm">
                                Attempt Now
                              </span>
                              <span className="text-[9px] font-bold text-black/80">
                                MTH 201 · Calculus
                              </span>
                            </div>
                          </div>

                          {/* Stats Grid */}
                          <div className="grid grid-cols-3 gap-2">
                            <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 text-center backdrop-blur-sm">
                              <div className="text-xl font-display font-black text-y leading-none">9</div>
                              <div className="text-[7px] font-black uppercase tracking-widest text-white/70 mt-1">
                                Notes
                              </div>
                            </div>
                            <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 text-center backdrop-blur-sm">
                              <div className="text-xl font-display font-black text-y leading-none">47</div>
                              <div className="text-[7px] font-black uppercase tracking-widest text-white/70 mt-1">
                                Asks
                              </div>
                            </div>
                            <div className="rounded-xl bg-white/5 border border-white/10 p-2.5 text-center backdrop-blur-sm">
                              <div className="text-xl font-display font-black text-y leading-none">3.8</div>
                              <div className="text-[7px] font-black uppercase tracking-widest text-white/70 mt-1">
                                CGPA
                              </div>
                            </div>
                          </div>

                          {/* Quick Tools */}
                          <div className="space-y-2 pt-1">
                            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-white/80 mb-2 px-1">
                              Quick Tools
                            </p>
                            <motion.button
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setPhoneScreen("ask")}
                              className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3.5 backdrop-blur-sm active:bg-white/10 transition-colors"
                            >
                              <div className="w-10 h-10 rounded-xl bg-y flex items-center justify-center shadow-lg shadow-y/20 shrink-0">
                                <BrainCircuit className="w-5 h-5 text-black" strokeWidth={2.5} />
                              </div>
                              <div className="flex-1 text-left">
                                <p className="text-[11px] font-black text-white leading-tight">
                                  Ask AI
                                </p>
                                <p className="text-[9px] font-semibold text-white/60 mt-0.5">
                                  Any course question
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-y/80" />
                            </motion.button>
                            <motion.button
                              whileTap={{ scale: 0.98 }}
                              onClick={() => setPhoneScreen("learn")}
                              className="w-full flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3.5 backdrop-blur-sm active:bg-white/10 transition-colors"
                            >
                              <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                                <BookOpen className="w-5 h-5 text-y" />
                              </div>
                              <div className="flex-1 text-left">
                                <p className="text-[11px] font-black text-white leading-tight">
                                  Notes
                                </p>
                                <p className="text-[9px] font-semibold text-white/60 mt-0.5">
                                  4 pages matched
                                </p>
                              </div>
                              <ChevronRight className="w-4 h-4 text-y/80" />
                            </motion.button>
                          </div>
                        </>
                      )}

                      {phoneScreen === "ask" && (
                        <>
                          {/* Ask Screen Header */}
                          <div className="flex items-center justify-between mb-3">
                            <button
                              onClick={() => setPhoneScreen("home")}
                              className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                            >
                              <ChevronLeft className="w-4 h-4 text-white" />
                            </button>
                            <span className="text-[11px] font-black uppercase tracking-widest text-y">
                              Ask AI
                            </span>
                            <div className="w-8" />
                          </div>

                          {/* Question Card */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/80 mb-2">
                              Your Question
                            </p>
                            <p className="text-sm font-black text-white leading-snug">
                              Calculate f&apos;(x) for 3x^4 - 2x^3 + 5x - 7, then evaluate at x = 2
                            </p>
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              <span className="rounded-full bg-y/20 border border-y/30 px-2.5 py-1 text-[8px] font-black uppercase tracking-wider text-y">
                                MST102
                              </span>
                              <span className="rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[8px] font-black uppercase tracking-wider text-white/70">
                                Level 200
                              </span>
                              <span className="rounded-full bg-emerald-500/20 border border-emerald-400/30 px-2.5 py-1 text-[8px] font-black uppercase tracking-wider text-emerald-300">
                                98% Confidence
                              </span>
                            </div>
                          </div>

                          {/* Answer Card */}
                          <div className="rounded-2xl bg-gradient-to-br from-y/10 to-y/5 border border-y/20 p-4">
                            <p className="text-[9px] font-black uppercase tracking-[0.25em] text-y mb-2">
                              Direct Explanation
                            </p>
                            <p className="text-xs font-semibold text-white leading-relaxed">
                              Use the power rule term by term: bring the index down, reduce the power by one, then substitute x = 2.
                            </p>
                          </div>

                          {/* Formula Card */}
                          <div className="rounded-2xl bg-black/40 border border-y/20 p-3.5">
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/70 mb-1.5">
                              General Pattern
                            </p>
                            <p className="font-mono text-xs font-black text-y">
                              d/dx[x^n] = n · x^(n-1)
                            </p>
                          </div>

                          {/* Steps Card */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-y mb-2">
                              Worked Steps
                            </p>
                            <p className="font-mono text-xs font-black text-white">
                              f&apos;(x) = 12x^3 - 6x^2 + 5
                            </p>
                            <p className="font-mono text-xs font-black text-white mt-1.5">
                              f&apos;(2) = 96 - 24 + 5 = 77
                            </p>
                          </div>

                          {/* Sources Card */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-3.5 backdrop-blur-sm">
                            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-y mb-1">
                              Sources
                            </p>
                            <p className="text-[10px] font-semibold text-white/80 leading-relaxed">
                              MST102 · Dept MST · Advanced Calculation Bank
                            </p>
                          </div>
                        </>
                      )}

                      {phoneScreen === "learn" && (
                        <>
                          {/* Learn Screen Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-8" />
                            <span className="text-[11px] font-black uppercase tracking-widest text-y">
                              My Notes
                            </span>
                            <button className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                              <Search className="w-4 h-4 text-white" />
                            </button>
                          </div>

                          {/* Note Card 1 */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-[9px] font-black uppercase tracking-wider text-y bg-y/10 px-2 py-0.5 rounded">
                                MTH 201
                              </span>
                              <span className="text-[9px] text-white/50">2h ago</span>
                            </div>
                            <h3 className="text-sm font-black text-white leading-tight mb-1.5">
                              Calculus - Integration Techniques
                            </h3>
                            <p className="text-[10px] text-white/60 leading-relaxed line-clamp-2">
                              Integration by parts, substitution methods, and partial fractions...
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <div className="h-1 flex-1 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full w-3/4 bg-y rounded-full" />
                              </div>
                              <span className="text-[9px] font-black text-y">75%</span>
                            </div>
                          </div>

                          {/* Note Card 2 */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-[9px] font-black uppercase tracking-wider text-y bg-y/10 px-2 py-0.5 rounded">
                                PHY 201
                              </span>
                              <span className="text-[9px] text-white/50">1d ago</span>
                            </div>
                            <h3 className="text-sm font-black text-white leading-tight mb-1.5">
                              Electromagnetic Waves
                            </h3>
                            <p className="text-[10px] text-white/60 leading-relaxed line-clamp-2">
                              Maxwell&apos;s equations, wave propagation, and polarization...
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <div className="h-1 flex-1 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full w-1/2 bg-y rounded-full" />
                              </div>
                              <span className="text-[9px] font-black text-y">50%</span>
                            </div>
                          </div>
                        </>
                      )}

                      {phoneScreen === "community" && (
                        <>
                          {/* Community Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div className="w-8" />
                            <span className="text-[11px] font-black uppercase tracking-widest text-y">
                              Community
                            </span>
                            <button className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                              <Users className="w-4 h-4 text-white" />
                            </button>
                          </div>

                          {/* Community Card */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-y to-y-light flex items-center justify-center shadow-lg">
                                <GraduationCap className="w-6 h-6 text-black" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-sm font-black text-white leading-tight">
                                  200L Academic
                                </h3>
                                <p className="text-[10px] text-white/60 mt-0.5">
                                  FUTO · 1,247 members
                                </p>
                              </div>
                            </div>
                            <p className="text-[10px] text-white/70 leading-relaxed">
                              Official 200 level community for academic discussions, notes sharing, and peer support.
                            </p>
                            <div className="mt-3 flex items-center gap-2">
                              <div className="flex -space-x-1.5">
                                <div className="w-6 h-6 rounded-full bg-y text-black flex items-center justify-center text-[8px] font-black border-2 border-[#0a0a0a]">
                                  JD
                                </div>
                                <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[8px] font-black border-2 border-[#0a0a0a]">
                                  AK
                                </div>
                                <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-[8px] font-black border-2 border-[#0a0a0a]">
                                  +5
                                </div>
                              </div>
                              <span className="text-[9px] text-white/50 ml-1">
                                23 online now
                              </span>
                            </div>
                          </div>

                          {/* Thread Preview */}
                          <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-7 h-7 rounded-full bg-y text-black flex items-center justify-center text-[9px] font-black">
                                TA
                              </div>
                              <div className="flex-1">
                                <p className="text-[11px] font-black text-white">Tolu A.</p>
                                <p className="text-[9px] text-white/50">Course Rep · 2m ago</p>
                              </div>
                            </div>
                            <p className="text-xs text-white/80 leading-relaxed">
                              Don&apos;t forget the thermodynamics assignment is due Friday. Check the pinned thread for the updated marking scheme.
                            </p>
                            <div className="mt-3 flex items-center gap-4 text-[10px] text-white/50">
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" />
                                12
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                5 replies
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </motion.div>
                  </AnimatePresence>
                  <div className="absolute bottom-0 left-0 right-0 flex justify-around border-t border-border bg-black/90 px-4 py-4 backdrop-blur">
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setPhoneScreen("home")}
                      className="flex flex-col items-center gap-1"
                    >
                      <GraduationCap
                        className={`w-5 h-5 ${phoneScreen === "home" ? "text-y" : "text-white"}`}
                      />
                      <span
                        className={`text-[7px] font-black uppercase tracking-widest ${phoneScreen === "home" ? "text-y" : "text-white"}`}
                      >
                        Home
                      </span>
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setPhoneScreen("ask")}
                      className="flex flex-col items-center gap-1"
                    >
                      <BrainCircuit
                        className={`w-5 h-5 ${phoneScreen === "ask" ? "text-y" : "text-white"}`}
                      />
                      <span
                        className={`text-[7px] font-black uppercase tracking-widest ${phoneScreen === "ask" ? "text-y" : "text-white"}`}
                      >
                        Ask
                      </span>
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setPhoneScreen("learn")}
                      className="flex flex-col items-center gap-1"
                    >
                      <BookOpen
                        className={`w-5 h-5 ${phoneScreen === "learn" ? "text-y" : "text-white"}`}
                      />
                      <span
                        className={`text-[7px] font-black uppercase tracking-widest ${phoneScreen === "learn" ? "text-y" : "text-white"}`}
                      >
                        Learn
                      </span>
                    </motion.button>
                    <motion.button
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setPhoneScreen("community")}
                      className="flex flex-col items-center gap-1"
                    >
                      <Globe
                        className={`w-5 h-5 ${phoneScreen === "community" ? "text-y" : "text-white"}`}
                      />
                      <span
                        className={`text-[7px] font-black uppercase tracking-widest ${phoneScreen === "community" ? "text-y" : "text-white"}`}
                      >
                        Community
                      </span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-card border border-border rounded-2xl p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-y/10 text-y flex items-center justify-center mb-4">
                    <BrainCircuit className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl font-black mb-3">
                    Ask from your notes only
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    Upload lecture notes, slides, and past questions. UniUI
                    retrieves from your materials first, cites pages, and
                    formats answers like a Nigerian professor would explain them
                    in class.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="bg-card border border-border rounded-2xl p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-y/10 text-y flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl font-black mb-3">
                    Turn uploads into a study library
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    Your PDFs become searchable notes, flashcards, formula
                    sheets, and revision cards that follow you across every
                    course and every semester.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="bg-card border border-border rounded-2xl p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-y/10 text-y flex items-center justify-center mb-4">
                    <Award className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl font-black mb-3">
                    Track readiness, not just activity
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    Daily questions, weak-topic detection, exam countdowns, and
                    CGPA-aware progress reports keep your preparation focused on
                    what actually matters.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- MAJESTIC_STUDY_MODE + CANNED_DEMO -- */}
      <SectionFade>
        <section className="relative py-24 px-6 bg-muted/30 overflow-hidden">
          {/* drifting accents */}
          <FloatGlyph
            className="pointer-events-none absolute left-[6%] top-[18%] font-mono text-2xl text-y/10"
            x={10}
            y={-14}
            duration={11}
          >
            ƒ(x)=x³
          </FloatGlyph>
          <FloatGlyph
            className="pointer-events-none absolute right-[7%] bottom-[16%] font-mono text-xl text-y/10"
            x={-10}
            y={14}
            duration={12}
            delay={1}
          >
            ∫ x² dx
          </FloatGlyph>
          <div className="relative mx-auto max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-center gap-12 lg:gap-16">
              <div className="max-w-2xl">
                <p className="text-xs font-black uppercase tracking-[0.25em] text-y mb-4">
                  Majestic Study Mode
                </p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mb-6 text-foreground">
                  Stop reading like a tourist.{" "}
                  <span className="text-y">
                    Study like the exam setter is watching.
                  </span>
                </h2>
                <p className="text-base text-foreground leading-relaxed mb-8">
                  UniUI converts scattered academic chaos into a precise command
                  centre: notes, answers, flashcards, leaderboards, and exam
                  intelligence in one clean interface.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    "Source-cited AI answers from your uploaded materials",
                    "Course rep libraries for official department materials",
                    "Exam readiness score before you touch the paper",
                  ].map((point) => (
                    <div
                      key={point}
                      className="flex items-start gap-2 rounded-xl border border-border bg-card p-4 text-sm font-medium text-foreground"
                    >
                      <Check className="mt-0.5 w-5 h-5 shrink-0 text-y" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Canned product demo — no backend calls */}
              <HeroProductDemo />
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- STUDY_TOOLS -- */}
      <SectionFade>
        <section id="tools" className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-4">
                {LANDING_COPY.tools.title}
              </h2>
              <p className="text-base text-foreground max-w-2xl mx-auto">
                {LANDING_COPY.tools.subtitle}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {LANDING_COPY.tools.items.map((tool, i) => (
                <motion.div
                  key={tool.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="group bg-card border border-border rounded-xl p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-y"
                >
                  <tool.icon className="w-8 h-8 text-y mx-auto mb-3 transition-transform group-hover:scale-110" />
                  <h3 className="font-bold text-sm text-foreground mb-1">
                    {tool.title}
                  </h3>
                  <p className="text-xs text-foreground">{tool.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- COMMUNITY -- */}
      <SectionFade>
        <section
          aria-busy={statsState === "loading"}
          className="py-24 px-6 bg-muted/30"
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-4">
                {LANDING_COPY.community.title}
              </h2>
              <p className="text-base text-foreground max-w-2xl mx-auto">
                {LANDING_COPY.community.subtitle}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center p-8 bg-card border border-border rounded-2xl">
                <Globe className="w-12 h-12 text-y mx-auto mb-4" />
                <div className="font-display text-4xl font-black text-y mb-2">
                  {statText(stats.people)}
                </div>
                <p className="text-sm font-semibold text-foreground">
                  Community Members
                </p>
              </div>
              <div className="text-center p-8 bg-card border border-border rounded-2xl">
                <BookOpen className="w-12 h-12 text-y mx-auto mb-4" />
                <div className="font-display text-4xl font-black text-y mb-2">
                  {statText(stats.courses)}
                </div>
                <p className="text-sm font-semibold text-foreground">
                  Course Libraries
                </p>
              </div>
              <div className="text-center p-8 bg-card border border-border rounded-2xl">
                <Award className="w-12 h-12 text-y mx-auto mb-4" />
                <div className="font-display text-4xl font-black text-y mb-2">
                  {statText(stats.students)}
                </div>
                <p className="text-sm font-semibold text-foreground">
                  Active Students
                </p>
              </div>
              <div className="text-center p-8 bg-card border border-border rounded-2xl">
                <NotebookPen className="w-12 h-12 text-y mx-auto mb-4" />
                <div className="font-display text-4xl font-black text-y mb-2">
                  {statText(stats.notes)}
                </div>
                <p className="text-sm font-semibold text-foreground">
                  Notes Available
                </p>
              </div>
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- TESTIMONIALS -- */}
      <SectionFade>
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-black text-center mb-16 tracking-tight">
              {LANDING_COPY.testimonials.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {LANDING_COPY.testimonials.items.map((t, i) => (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-card border border-border rounded-2xl p-6 space-y-4"
                >
                  <div className="flex gap-1">
                    {[...Array(t.rating)].map((_, j) => (
                      <Award key={j} className="w-4 h-4 text-y fill-y" />
                    ))}
                  </div>
                  <p className="text-sm italic text-foreground leading-relaxed">
                    “{t.quote}”
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-y flex items-center justify-center font-bold text-xs text-black">
                      {t.name
                        .split(" ")
                        .map((w) => w[0])
                        .join("")}
                    </div>
                    <div>
                      <p className="font-bold text-sm text-foreground">
                        {t.name}
                      </p>
                      <p className="text-xs text-foreground">{t.school}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- FAQ -- */}
      <SectionFade>
        <section className="py-24 px-6 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-4">
                {LANDING_COPY.FAQ.title}
              </h2>
              <p className="text-base text-foreground">
                {LANDING_COPY.FAQ.subtitle}
              </p>
            </div>
            <div className="space-y-4">
              {LANDING_COPY.FAQ.questions.map((q) => (
                <details
                  key={q.question}
                  className="group bg-card border border-border rounded-xl"
                >
                  <summary className="cursor-pointer p-5 font-bold text-sm text-foreground hover:text-y transition-colors flex items-center justify-between">
                    {q.question}
                    <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="px-5 pb-5 text-sm text-foreground">
                    {q.answer}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- INSTITUTIONAL_TRUST -- */}
      <SectionFade>
        <section className="py-24 px-6 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-display text-3xl md:text-5xl font-black text-center mb-16 tracking-tight">
              {LANDING_COPY.trust.title}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
              {trustStats.map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className="flex flex-col items-center justify-center p-8 rounded-2xl bg-card border border-border text-center transition-all duration-300 hover:border-y hover:shadow-[0_10px_40px_rgba(0,0,0,0.12)]"
                >
                  <div className="font-display text-3xl md:text-4xl font-black text-y mb-3 tabular-nums">
                    {stat.display}
                  </div>
                  <p className="text-xs md:text-sm font-semibold text-foreground text-center leading-snug max-w-[180px]">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </SectionFade>

      {/* -- ACADEMIC_CTA -- */}
      <SectionFade>
        <section className="relative py-24 px-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-y via-y-light to-y" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.2),transparent_40%)]" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="relative z-10 max-w-3xl mx-auto text-center"
          >
            <h2 className="font-display text-3xl md:text-5xl font-black mb-8 text-black tracking-tight">
              {LANDING_COPY.cta_banner.headline}
            </h2>
            <Link
              href="/auth/signup"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-black text-white font-bold text-sm uppercase tracking-[0.15em] rounded-xl transition-all hover:shadow-[0_0_32px_rgba(0,0,0,0.3)] hover:ring-2 hover:ring-black/40 hover:scale-105"
            >
              {LANDING_COPY.cta_banner.cta}
              <ChevronRight className="w-4 h-4" />
            </Link>
            <p className="mt-6 text-foreground text-xs font-semibold tracking-wide">
              {LANDING_COPY.cta_banner.fine_print}
            </p>
          </motion.div>
        </section>
      </SectionFade>

      {/* -- SCHOLARLY_FOOTER -- */}
      <footer className="border-t border-border bg-background">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex flex-col md:flex-row justify-between gap-12">
            {/* Brand */}
            <div className="max-w-xs">
              <div className="font-display text-xl font-black tracking-tight mb-3">
                {LANDING_COPY.footer.tagline}
              </div>
              <p className="text-sm text-foreground leading-relaxed">
                Advanced academic infrastructure designed for rigorous
                scholarship — built for students at universities across Nigeria.
              </p>
            </div>

            {/* Links */}
            <div className="flex flex-col sm:flex-row gap-12">
              <div>
                <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-foreground mb-4">
                  Resources
                </h4>
                <ul className="space-y-3">
                  {LANDING_COPY.footer.links.map((link) => (
                    <li key={link}>
                      <Link
                        href={FOOTER_LINK_HREF[link] || "#"}
                        className="text-sm font-semibold text-foreground hover:text-y transition-colors"
                      >
                        {link}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-foreground font-semibold tracking-wide">
              {LANDING_COPY.footer.copyright}
            </p>
            <div className="flex gap-6">
              {LANDING_COPY.footer.links.map((link) => (
                <Link
                  key={link}
                  href={FOOTER_LINK_HREF[link] || "#"}
                  className="text-xs text-foreground hover:text-y transition-colors font-semibold"
                >
                  {link}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
